import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { Button } from '@/components/shared/button/Button';
import { TSTitleCard } from '@/components/shared/card/Card';
import { ConfirmDialog } from '@/components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '@/helpers/toastHelper';
import { useAvailabilityExceptionRequest } from '@/services/availability/useAvailabilityExceptionRequest';
import { Feature } from '@/services/backstage/Feature';
import { useCancelAvailabilityExceptionRequestMutation, useGetMeQuery } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { FunctionComponent, useState } from 'react';
import { FiBell } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { AvailabilityExceptionLeaveRequestDetailsCard } from './AvailabilityExceptionLeaveRequestDetailsCard';

const MyAvailabilityExceptionLeaveRequestDetailsPage: FunctionComponent = () => {
  const { requestId } = useParams<'requestId'>();
  const navigate = useNavigate();

  const [{ data: meData }] = useGetMeQuery();
  const employeeId = meData?.myEmployee?.identityId;

  const { request, refetch } = useAvailabilityExceptionRequest(employeeId, requestId);

  const [cancelResult, cancelRequest] = useCancelAvailabilityExceptionRequestMutation();
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);

  const isPending = request?.exceptions?.some(exception => exception.approval === 'Requested');

  const withdrawRequest = async () => {
    if (!employeeId || !requestId || !isPending) return;

    const result = await cancelRequest({
      requestId
    });

    await handleMutationResult(result, {
      successMessage: isLeaveRequest ? 'Leave request withdrawn.' : 'Availability request withdrawn.',
      onSuccess: async () => {
        await refetch({ requestPolicy: 'network-only' });
        setTimeout(() => navigate('/ess/my-calendar'), 100);
      }
    });
  };

  if (!request) {
    return <p className="p-6">Availability request not found.</p>;
  }

  const exception = request?.exceptions?.[0];
  const isLeaveRequest = !!exception?.leaveType;

  const pageTitle = isLeaveRequest ? 'Leave Request' : 'Update availability request';
  const withdrawTitle = isLeaveRequest ? 'Withdraw leave request' : 'Withdraw availability request';
  const withdrawMessage = isLeaveRequest
    ? 'Are you sure you want to withdraw this leave request?'
    : 'Are you sure you want to withdraw this availability request?';

  return (
    <Feature name="requests">
      <PageContainer>
        <PageSizer mode={SizerMode.PAGE} className="justify-items-center">
          <TSTitleCard className="w-full space-y-5 p-5" title={pageTitle} icon={FiBell}>
            <AvailabilityExceptionLeaveRequestDetailsCard
              request={request}
              actions={
                <div className="flex justify-end space-x-3">
                  <Button big onClick={() => navigate(-1)}>
                    {strings.common.back}
                  </Button>

                  {isPending && (
                    <Button big danger onClick={() => setWithdrawDialogOpen(true)} disabled={cancelResult.fetching}>
                      Withdraw
                    </Button>
                  )}
                </div>
              }
            />
          </TSTitleCard>
        </PageSizer>
      </PageContainer>

      <ConfirmDialog
        open={withdrawDialogOpen}
        onClose={() => setWithdrawDialogOpen(false)}
        onConfirm={withdrawRequest}
        title={withdrawTitle}
        confirmLabel="Withdraw"
        cancelLabel={strings.common.cancel}
        dangerous
        loading={cancelResult.fetching}
      >
        {withdrawMessage}
      </ConfirmDialog>
    </Feature>
  );
};

export default MyAvailabilityExceptionLeaveRequestDetailsPage;
